import { useState, useEffect } from "react";

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const GITHUB_URL = "https://github.com/diegoneuber/S4T-Framework";
const FEEDBACK_EMAIL = "feedback@sec4.tech";
const METRICS_URL = "https://sec4.tech/assessment/metrics.php";
const LEADS_URL = "https://sec4.tech/assessment/leads.php";
const PRIVACY_URL = "https://sec4.tech/privacy";
const METRICS_THRESHOLD = 5;

const SCALE = 1.15;

const fs = (px) => `${px * SCALE}px`;

// ─── STATIC RECOMMENDATIONS ─────────────────────────────────────────────────
const RECS = {
  en: [
    {
      low:  { title: "Establish a Formal Security Governance Structure", priority: "High", description: "Your organization lacks the foundational governance layer required by the S4T Framework. Start by defining a security policy that covers data classification, acceptable use, incident response roles, and compliance requirements. Assign clear ownership — ideally a CISO or security lead — and ensure executive leadership receives monthly security briefings. Without governance, all other security investments lack direction and accountability." },
      mid:  { title: "Mature Your Security Governance Program", priority: "Medium", description: "Your governance foundation exists but needs maturity. Formalize your risk appetite statement with board-level sign-off, establish quarterly security reviews with C-level leadership, and implement a security awareness training program with measurable completion rates. Consider aligning your governance structure with ISO 27001 or NIST CSF to provide a recognized framework for auditors and clients." },
      high: { title: "Optimize Executive Security Alignment", priority: "Low", description: "Your governance layer is strong. Focus now on continuous improvement: automate executive reporting dashboards, integrate security KPIs into business performance reviews, and establish a formal security culture program. Consider pursuing ISO 27001 certification to externally validate your governance maturity." }
    },
    {
      low:  { title: "Implement Threat Intelligence and Vulnerability Management", priority: "High", description: "Your organization is operating without visibility into the threat landscape. Implement a vulnerability scanning solution (Nessus, OpenVAS, or similar) on a weekly schedule, subscribe to at least one threat intelligence feed relevant to your sector, and establish a CVE prioritization process using CVSS scores combined with business context. Without this layer, your controls are reactive rather than risk-driven." },
      mid:  { title: "Strengthen Threat Intelligence Integration", priority: "Medium", description: "You have basic vulnerability management but lack integration with operational processes. Connect your threat intelligence feeds to your SIEM for automated correlation, implement a formal risk register updated monthly, and establish a third-party risk monitoring process for critical vendors. The S4T RI layer should feed directly into your Control Framework priorities." },
      high: { title: "Advance to Predictive Threat Modeling", priority: "Low", description: "Your risk intelligence is solid. Advance to predictive capabilities: implement attack surface management (ASM) tooling, integrate threat intelligence into automated playbooks, and establish a formal threat modeling process for new projects and infrastructure changes." }
    },
    {
      low:  { title: "Build a Risk-Prioritized Control Framework", priority: "High", description: "Your organization lacks a structured control framework. Begin by completing an asset inventory — you cannot protect what you cannot see. Map your existing controls against CIS Controls v8 (Tier 1 as minimum baseline) to identify critical gaps. Establish configuration baselines for all major systems and implement a change management process. This layer is the backbone of the S4T Framework's measurability." },
      mid:  { title: "Optimize Control Coverage and Measurement", priority: "Medium", description: "Your control framework exists but coverage is incomplete. Conduct a formal gap analysis against ISO 27001 Annex A or NIST CSF to identify unmapped risks. Implement automated configuration compliance monitoring (CIS-CAT or similar) and establish a formal exceptions management process. Ensure your asset inventory is updated automatically rather than manually." },
      high: { title: "Automate Control Compliance Monitoring", priority: "Low", description: "Your control framework is mature. Focus on automation: implement continuous compliance monitoring integrated with your CMDB, automate control evidence collection for audits, and establish a risk-based control prioritization model that dynamically adjusts based on threat intelligence inputs." }
    },
    {
      low:  { title: "Deploy Core Operational Security Capabilities", priority: "High", description: "Your operational security posture is critically underdeveloped. Prioritize in this order: deploy EDR/XDR on all endpoints, implement a SIEM with basic use-cases (failed logins, privilege escalation, lateral movement), establish a formal patch management process with defined SLAs, and document an incident response plan. The S4T OS layer directly determines your Mean Time to Respond — currently your longest exposure window." },
      mid:  { title: "Strengthen SOC Capabilities and Response Velocity", priority: "High", description: "You have operational security tools deployed but response maturity is limited. Focus on SIEM tuning to reduce alert fatigue, implement MFA across all critical systems and remote access, establish formal incident response runbooks for your top 5 threat scenarios, and test your DR capabilities with a tabletop exercise. Target a Mean Time to Respond under 4 hours for critical incidents." },
      high: { title: "Advance Toward Automated Response Orchestration", priority: "Low", description: "Your operational security is strong. Implement SOAR capabilities to automate repetitive response tasks, integrate your EDR, SIEM, and ticketing systems into unified workflows, and establish formal SLAs for each incident severity tier." }
    },
    {
      low:  { title: "Establish Continuous Validation Practices", priority: "High", description: "Without validation, you cannot measure whether your controls actually work. Conduct a penetration test immediately — prioritizing external perimeter and social engineering — and schedule quarterly assessments going forward. Deploy an open-source BAS tool (Atomic Red Team, Caldera) to continuously test detection coverage. The S4T Va(t) metric directly reduces your risk trajectory." },
      mid:  { title: "Advance Toward Continuous Control Testing", priority: "Medium", description: "You conduct periodic testing but lack continuous validation. Implement a BAS platform integrated with your SIEM to automatically test detection rules weekly. Establish purple team exercises quarterly. Track your control coverage rate as a KPI — the S4T Framework targets 80%+ coverage for Mature status." },
      high: { title: "Implement Full BAS and Adversary Simulation", priority: "Low", description: "Your validation program is mature. Advance to adversary simulation: implement a continuous BAS platform mapped to MITRE ATT&CK, establish a formal red team program with defined rules of engagement, and integrate validation results into your risk model to automatically recalibrate the S4T Security Score." }
    },
    {
      low:  { title: "Build a Security Metrics and Feedback Program", priority: "Medium", description: "Your security program lacks the feedback mechanisms required for continuous improvement. Define 5-7 core security KPIs aligned with the S4T Security Score: control coverage rate, MTTR, patch SLA compliance, training completion rate, and vulnerability aging. Implement a formal post-incident review process and maintain a lessons-learned repository. Without this layer, your security program cannot self-correct." },
      mid:  { title: "Formalize Your Security Program Roadmap", priority: "Medium", description: "You collect some metrics but lack a structured improvement program. Build a 12-month security roadmap with quarterly milestones mapped to your target S4T Security Score. Establish a security steering committee that reviews metrics monthly and adjusts priorities based on threat landscape changes." },
      high: { title: "Implement Predictive Security Analytics", priority: "Low", description: "Your adaptive learning layer is strong. Implement ML-based anomaly detection on your security metrics to identify trends before they become incidents, and integrate your S4T Security Score into business risk reporting for board-level visibility." }
    }
  ],
  pt: [
    {
      low:  { title: "Estabeleça uma Estrutura Formal de Governança de Segurança", priority: "Alta", description: "Sua organização não possui a camada de governança fundamental exigida pelo S4T Framework. Comece definindo uma política de segurança que cubra classificação de dados, uso aceitável, papéis na resposta a incidentes e requisitos de conformidade. Atribua responsabilidade clara — idealmente um CISO — e garanta que a liderança executiva receba briefings mensais. Sem governança, todos os outros investimentos em segurança carecem de direção." },
      mid:  { title: "Amadureça seu Programa de Governança de Segurança", priority: "Média", description: "Sua base de governança existe, mas precisa de maturidade. Formalize sua declaração de apetite de risco com aprovação do board, estabeleça revisões trimestrais com a liderança C-level e implemente um programa de treinamento de conscientização com taxas de conclusão mensuráveis. Considere alinhar sua estrutura com ISO 27001 ou NIST CSF." },
      high: { title: "Otimize o Alinhamento Executivo de Segurança", priority: "Baixa", description: "Sua camada de governança é sólida. Foque em melhoria contínua: automatize dashboards de relatórios executivos, integre KPIs de segurança nas revisões de desempenho do negócio e estabeleça um programa formal de cultura de segurança. Considere buscar a certificação ISO 27001." }
    },
    {
      low:  { title: "Implante Inteligência de Ameaças e Gestão de Vulnerabilidades", priority: "Alta", description: "Sua organização opera sem visibilidade sobre o panorama de ameaças. Implemente uma solução de escaneamento de vulnerabilidades (Nessus, OpenVAS ou similar) em ciclo semanal, assine pelo menos um feed de inteligência de ameaças relevante para seu setor e estabeleça um processo de priorização de CVEs usando scores CVSS combinados com contexto de negócio." },
      mid:  { title: "Fortaleça a Integração de Inteligência de Ameaças", priority: "Média", description: "Você tem gestão básica de vulnerabilidades, mas falta integração com processos operacionais. Conecte seus feeds de inteligência ao SIEM para correlação automatizada, implemente um registro de riscos formal atualizado mensalmente e estabeleça monitoramento de risco de terceiros para fornecedores críticos." },
      high: { title: "Avance para Modelagem Preditiva de Ameaças", priority: "Baixa", description: "Sua inteligência de risco é sólida. Avance para capacidades preditivas: implante ferramentas de gestão de superfície de ataque (ASM), integre feeds de inteligência a playbooks automatizados e estabeleça um processo formal de modelagem de ameaças para novos projetos." }
    },
    {
      low:  { title: "Construa um Framework de Controles Baseado em Risco", priority: "Alta", description: "Sua organização não possui um framework de controles estruturado. Comece completando um inventário de ativos — você não pode proteger o que não consegue ver. Mapeie seus controles existentes contra CIS Controls v8 para identificar lacunas críticas. Estabeleça baselines de configuração e implemente um processo de gestão de mudanças." },
      mid:  { title: "Otimize a Cobertura e Medição de Controles", priority: "Média", description: "Seu framework existe, mas a cobertura é incompleta. Realize uma análise formal de gaps contra ISO 27001 Anexo A ou NIST CSF. Implemente monitoramento automatizado de conformidade (CIS-CAT ou similar) e estabeleça um processo formal de gestão de exceções. Garanta que seu inventário de ativos seja atualizado automaticamente." },
      high: { title: "Automatize o Monitoramento de Conformidade de Controles", priority: "Baixa", description: "Seu framework de controles é maduro. Foque em automação: implante monitoramento contínuo de conformidade integrado ao seu CMDB e automatize a coleta de evidências de controles para auditorias." }
    },
    {
      low:  { title: "Implante Capacidades Essenciais de Segurança Operacional", priority: "Alta", description: "Sua postura operacional está criticamente subdesenvolvida. Priorize: implante EDR/XDR em todos os endpoints, implemente SIEM com use-cases básicos, estabeleça gestão formal de patches com SLAs e documente um plano de resposta a incidentes." },
      mid:  { title: "Fortaleça Capacidades de SOC e Velocidade de Resposta", priority: "Alta", description: "Você tem ferramentas implantadas, mas a maturidade de resposta é limitada. Foque no tuning do SIEM para reduzir fadiga de alertas, implante MFA em todos os sistemas críticos, estabeleça runbooks formais de IR para seus 5 principais cenários e teste suas capacidades de DR com um tabletop exercise." },
      high: { title: "Avance para Orquestração Automatizada de Resposta", priority: "Baixa", description: "Sua segurança operacional é sólida. Implemente capacidades SOAR para automatizar tarefas repetitivas de resposta e integre seu EDR, SIEM e sistema de tickets em workflows unificados." }
    },
    {
      low:  { title: "Estabeleça Práticas de Validação Contínua", priority: "Alta", description: "Sem validação, você não mede se seus controles funcionam. Realize um pentest imediatamente — priorizando perímetro externo e engenharia social — e agende avaliações trimestrais. Implante uma ferramenta BAS open-source (Atomic Red Team, Caldera) para testar continuamente a cobertura de detecção." },
      mid:  { title: "Avance em Direção ao Teste Contínuo de Controles", priority: "Média", description: "Você realiza testes periódicos, mas falta validação contínua. Implante uma plataforma BAS integrada ao SIEM para testar regras de detecção semanalmente. Estabeleça purple team exercises trimestrais e acompanhe sua taxa de cobertura de controles — meta: 80%+ para status Maduro." },
      high: { title: "Implante BAS Completo e Simulação de Adversários", priority: "Baixa", description: "Seu programa de validação é maduro. Avance para simulação de adversários: implante BAS contínuo mapeado ao MITRE ATT&CK e estabeleça um programa formal de red team com regras de engajamento definidas." }
    },
    {
      low:  { title: "Construa um Programa de Métricas e Feedback de Segurança", priority: "Média", description: "Seu programa carece de mecanismos de feedback para melhoria contínua. Defina 5-7 KPIs centrais alinhados ao Security Score S4T: taxa de cobertura de controles, MTTR, conformidade com SLA de patches, taxa de conclusão de treinamentos e aging de vulnerabilidades. Implemente revisão pós-incidente formal." },
      mid:  { title: "Formalize o Roadmap do seu Programa de Segurança", priority: "Média", description: "Você coleta algumas métricas, mas falta programa estruturado. Construa um roadmap de 12 meses com marcos trimestrais mapeados ao seu Security Score S4T alvo. Estabeleça um comitê de segurança que revise métricas mensalmente." },
      high: { title: "Implante Analytics Preditivos de Segurança", priority: "Baixa", description: "Sua camada de aprendizado é sólida. Avance para analytics preditivos: implante detecção de anomalias nas suas métricas de segurança para identificar tendências antes que se tornem incidentes, e integre o Security Score S4T nos relatórios de risco do negócio." }
    }
  ]
};

const LAYER_NAMES = {
  en: ["Governance","Risk Intelligence","Control Framework","Operational Security","Validation & Assurance","Adaptive Learning"],
  pt: ["Governança","Inteligência de Risco","Framework de Controles","Segurança Operacional","Validação & Assurance","Aprendizado Adaptativo"]
};

function getStaticRecommendations(L, lang) {
  return L.map((score, i) => {
    const tier = score < 0.45 ? "low" : score < 0.75 ? "mid" : "high";
    return { ...RECS[lang][i][tier], layer: LAYER_NAMES[lang][i], score };
  }).sort((a, b) => a.score - b.score).slice(0, 3);
}

// ─── TRANSLATIONS ────────────────────────────────────────────────────────────
const T = {
  en: {
    title: "S4T Framework", subtitle: "Security Assessment Tool",
    tagline: "Measure. Understand. Improve.",
    startExecutive: "Executive Assessment", startTechnical: "Technical Assessment",
    execDesc: "20 questions · Business language · 5 minutes",
    techDesc: "33 questions · Technical depth · 15 minutes",
    next: "Next", back: "Back", finish: "Calculate Score", restart: "New Assessment",
    yourScore: "Your Security Score",
    scoreLabel: ["Initial","Developing","Mature","Optimized"],
    layers: ["Governance","Risk Intelligence","Control Framework","Operational Security","Validation & Assurance","Adaptive Learning"],
    layerDesc: ["Strategy, policies & executive alignment","Threat monitoring & vulnerability management","Risk-prioritized controls & asset inventory","SOC, EDR, SIEM & incident response","Testing, BAS & continuous validation","Feedback, analytics & recalibration"],
    recommendations: "Recommendations",
    metrics: "Global Metrics", totalAssessments: "Total Assessments",
    countries: "Countries", avgScore: "Avg Score", switchLang: "Português",
    yes: "Yes", no: "No",
    scaleLabels: ["None","Initial","Developing","Mature","Optimized"],
    actions: "Share & Export",
    emailResults: "Email Results",
    downloadPdf: "Download PDF",
    shareLink: "Copy Link",
    feedback: "Feedback",
    viewGithub: "GitHub",
    contributeGithub: "Contribute on GitHub",
    benchmarkBelow: "Below Mature threshold (65%)",
    benchmarkAbove: "Above Mature threshold",
    sharedNotice: "You are viewing shared results. Take your own assessment below.",
    takeYourOwn: "Take My Own Assessment",
    linkCopied: "Link copied!",
    openSource: "S4T is a free and open source cybersecurity framework.",
    socialProof: "Join {N} organizations across {C} countries measuring their cybersecurity maturity with S4T.",
    emailSubject: "S4T Framework — My Cybersecurity Maturity Results",
    leadTitle: "Get your full report",
    leadDesc: "Receive the complete PDF report with a detailed action plan for all six layers, plus a personalized review of your results from our team.",
    leadName: "Name", leadEmail: "Work email", leadCompany: "Company", leadPhone: "Phone (optional)",
    leadConsent: "I agree to be contacted by Sec4Tech about my results and have read the",
    leadPrivacy: "Privacy Policy",
    leadSubmit: "Send my report",
    leadSending: "Sending…",
    leadOk: "Done! Check your inbox shortly — we'll be in touch.",
    leadErr: "Couldn't send right now. Please try again or email contact@sec4.tech.",
    leadConsentReq: "Please accept the consent to continue.",
    frameworkArch: "Framework Architecture",
    maturityBench: "S4T Maturity Benchmarks",
    resultsLabel: "S4T Assessment Results",
    execQuestions: [
      {layer:0,text:"Does your organization have a formally documented security policy?"},
      {layer:0,text:"Does senior leadership receive regular security reports?"},
      {layer:0,text:"Is there a defined risk tolerance / risk appetite?"},
      {layer:0,text:"Do employees receive security awareness training?"},
      {layer:1,text:"Is your organization actively monitoring for external threats?"},
      {layer:1,text:"Are vulnerabilities identified and prioritized systematically?"},
      {layer:1,text:"Is there a business impact assessment process?"},
      {layer:2,text:"Are security controls mapped to a framework (ISO, NIST, CIS)?"},
      {layer:2,text:"Is there a periodic review process for security controls?"},
      {layer:2,text:"Is there an up-to-date asset inventory?"},
      {layer:3,text:"Is there active 24/7 monitoring or a SOC?"},
      {layer:3,text:"Is endpoint protection (EDR/XDR) deployed across all devices?"},
      {layer:3,text:"Is there a formally documented and tested incident response plan?"},
      {layer:3,text:"Is there a formal patch management process?"},
      {layer:4,text:"Are penetration tests conducted periodically?"},
      {layer:4,text:"Are security controls tested automatically or via red team exercises?"},
      {layer:4,text:"Is there continuous monitoring of control effectiveness?"},
      {layer:5,text:"Are security metrics collected and analyzed regularly?"},
      {layer:5,text:"Are lessons learned from incidents incorporated into the program?"},
      {layer:5,text:"Does the security program evolve based on data and feedback?"},
    ],
    techQuestions: [
      {layer:0,text:"Security policy coverage and review frequency (1=none, 5=quarterly reviewed)"},
      {layer:0,text:"Executive security reporting cadence and depth"},
      {layer:0,text:"Risk appetite definition and board-level alignment"},
      {layer:0,text:"Security awareness training program maturity"},
      {layer:0,text:"Regulatory compliance program coverage"},
      {layer:1,text:"Threat intelligence feeds and integration quality"},
      {layer:1,text:"Vulnerability scanning frequency and coverage"},
      {layer:1,text:"CVE prioritization methodology maturity"},
      {layer:1,text:"Business impact analysis process maturity"},
      {layer:1,text:"Third-party/supply chain risk monitoring"},
      {layer:2,text:"Control framework alignment (ISO 27001, NIST CSF, CIS Controls)"},
      {layer:2,text:"Asset inventory completeness and update frequency"},
      {layer:2,text:"Security hardening baseline coverage"},
      {layer:2,text:"Configuration management process maturity"},
      {layer:2,text:"Data classification and handling procedures"},
      {layer:3,text:"SOC capability and coverage hours"},
      {layer:3,text:"EDR/XDR deployment coverage and alert tuning"},
      {layer:3,text:"SIEM implementation and use-case coverage"},
      {layer:3,text:"Patch management SLA compliance rate"},
      {layer:3,text:"Identity and access management maturity (MFA, PAM, SSO)"},
      {layer:3,text:"Network segmentation and Zero Trust implementation"},
      {layer:3,text:"Incident response plan completeness and test frequency"},
      {layer:3,text:"Backup and disaster recovery coverage and test results"},
      {layer:4,text:"Penetration testing frequency and scope"},
      {layer:4,text:"BAS or automated red team exercise maturity"},
      {layer:4,text:"Continuous control monitoring automation"},
      {layer:4,text:"Vulnerability remediation SLA compliance"},
      {layer:4,text:"Purple team / threat hunting capability"},
      {layer:5,text:"Security metrics program completeness"},
      {layer:5,text:"Post-incident review and lessons learned process"},
      {layer:5,text:"Security program roadmap and KPI tracking"},
      {layer:5,text:"Threat model update frequency"},
      {layer:5,text:"Security tool consolidation and optimization"},
    ],
  },
  pt: {
    title: "S4T Framework", subtitle: "Ferramenta de Assessment",
    tagline: "Meça. Entenda. Evolua.",
    startExecutive: "Assessment Executivo", startTechnical: "Assessment Técnico",
    execDesc: "20 perguntas · Linguagem de negócio · 5 minutos",
    techDesc: "33 perguntas · Profundidade técnica · 15 minutos",
    next: "Próxima", back: "Voltar", finish: "Calcular Score", restart: "Novo Assessment",
    yourScore: "Seu Security Score",
    scoreLabel: ["Inicial","Em Desenvolvimento","Maduro","Otimizado"],
    layers: ["Governança","Inteligência de Risco","Framework de Controles","Segurança Operacional","Validação & Assurance","Aprendizado Adaptativo"],
    layerDesc: ["Estratégia, políticas e alinhamento executivo","Monitoramento de ameaças e gestão de vulnerabilidades","Controles priorizados por risco e inventário de ativos","SOC, EDR, SIEM e resposta a incidentes","Testes, BAS e validação contínua","Feedback, analytics e recalibração"],
    recommendations: "Recomendações",
    metrics: "Métricas Globais", totalAssessments: "Total de Assessments",
    countries: "Países", avgScore: "Score Médio", switchLang: "English",
    yes: "Sim", no: "Não",
    scaleLabels: ["Nenhum","Inicial","Em Dev.","Maduro","Otimizado"],
    actions: "Compartilhar & Exportar",
    emailResults: "Enviar por E-mail",
    downloadPdf: "Baixar PDF",
    shareLink: "Copiar Link",
    feedback: "Feedback",
    viewGithub: "GitHub",
    contributeGithub: "Contribuir no GitHub",
    benchmarkBelow: "Abaixo do limiar Maduro (65%)",
    benchmarkAbove: "Acima do limiar Maduro",
    sharedNotice: "Você está vendo um resultado compartilhado. Faça seu próprio assessment abaixo.",
    takeYourOwn: "Fazer Meu Assessment",
    linkCopied: "Link copiado!",
    openSource: "S4T é um framework de cibersegurança gratuito e open source.",
    socialProof: "Junte-se a {N} organizações em {C} países que mediram sua maturidade com o S4T.",
    emailSubject: "S4T Framework — Meu Resultado de Maturidade em Cibersegurança",
    leadTitle: "Receba seu relatório completo",
    leadDesc: "Receba o relatório completo em PDF com um plano de ação detalhado para as seis camadas, além de uma análise personalizada dos seus resultados feita pela nossa equipe.",
    leadName: "Nome", leadEmail: "E-mail corporativo", leadCompany: "Empresa", leadPhone: "Telefone (opcional)",
    leadConsent: "Concordo em ser contatado pela Sec4Tech sobre meus resultados e li a",
    leadPrivacy: "Política de Privacidade",
    leadSubmit: "Enviar meu relatório",
    leadSending: "Enviando…",
    leadOk: "Pronto! Verifique seu e-mail em instantes, entraremos em contato.",
    leadErr: "Não foi possível enviar agora. Tente novamente ou escreva para contact@sec4.tech.",
    leadConsentReq: "Por favor aceite o consentimento para continuar.",
    frameworkArch: "Arquitetura do Framework",
    maturityBench: "Níveis de Maturidade S4T",
    resultsLabel: "Resultado do Assessment S4T",
    execQuestions: [
      {layer:0,text:"Sua organização possui uma política de segurança formalmente documentada?"},
      {layer:0,text:"A liderança sênior recebe relatórios de segurança regularmente?"},
      {layer:0,text:"Existe uma definição formal de tolerância ao risco (risk appetite)?"},
      {layer:0,text:"Os colaboradores recebem treinamento de conscientização em segurança?"},
      {layer:1,text:"Sua organização monitora ativamente ameaças externas?"},
      {layer:1,text:"Vulnerabilidades são identificadas e priorizadas sistematicamente?"},
      {layer:1,text:"Existe um processo de avaliação de impacto ao negócio?"},
      {layer:2,text:"Os controles de segurança estão mapeados contra um framework (ISO, NIST, CIS)?"},
      {layer:2,text:"Existe um processo de revisão periódica dos controles de segurança?"},
      {layer:2,text:"Há um inventário de ativos atualizado?"},
      {layer:3,text:"Existe monitoramento ativo 24/7 ou um SOC?"},
      {layer:3,text:"Proteção de endpoint (EDR/XDR) está implantada em todos os dispositivos?"},
      {layer:3,text:"Existe um plano de resposta a incidentes documentado e testado?"},
      {layer:3,text:"Existe um processo formal de gestão de patches?"},
      {layer:4,text:"Testes de penetração são realizados periodicamente?"},
      {layer:4,text:"Controles de segurança são testados automaticamente ou via red team?"},
      {layer:4,text:"Existe monitoramento contínuo da efetividade dos controles?"},
      {layer:5,text:"Métricas de segurança são coletadas e analisadas regularmente?"},
      {layer:5,text:"Lições aprendidas de incidentes são incorporadas ao programa?"},
      {layer:5,text:"O programa de segurança evolui baseado em dados e feedback?"},
    ],
    techQuestions: [
      {layer:0,text:"Cobertura e frequência de revisão da política de segurança (1=nenhuma, 5=revisão trimestral)"},
      {layer:0,text:"Cadência e profundidade dos relatórios de segurança para executivos"},
      {layer:0,text:"Definição de apetite de risco e alinhamento com o board"},
      {layer:0,text:"Maturidade do programa de treinamento de conscientização"},
      {layer:0,text:"Cobertura do programa de conformidade regulatória"},
      {layer:1,text:"Qualidade e integração dos feeds de inteligência de ameaças"},
      {layer:1,text:"Frequência e cobertura do escaneamento de vulnerabilidades"},
      {layer:1,text:"Maturidade da metodologia de priorização de CVEs"},
      {layer:1,text:"Maturidade do processo de análise de impacto ao negócio"},
      {layer:1,text:"Monitoramento de risco de terceiros e cadeia de suprimentos"},
      {layer:2,text:"Alinhamento com framework de controles (ISO 27001, NIST CSF, CIS Controls)"},
      {layer:2,text:"Completude e frequência de atualização do inventário de ativos"},
      {layer:2,text:"Cobertura de baselines de hardening de segurança"},
      {layer:2,text:"Maturidade do processo de gestão de configuração"},
      {layer:2,text:"Procedimentos de classificação e tratamento de dados"},
      {layer:3,text:"Capacidade e cobertura horária do SOC"},
      {layer:3,text:"Cobertura de deployment do EDR/XDR e tuning de alertas"},
      {layer:3,text:"Implementação do SIEM e cobertura de use-cases"},
      {layer:3,text:"Taxa de conformidade com SLA de gestão de patches"},
      {layer:3,text:"Maturidade de IAM (MFA, PAM, SSO)"},
      {layer:3,text:"Segmentação de rede e implementação de Zero Trust"},
      {layer:3,text:"Completude do plano de IR e frequência de testes"},
      {layer:3,text:"Cobertura e resultados de testes de backup e DR"},
      {layer:4,text:"Frequência e escopo dos testes de penetração"},
      {layer:4,text:"Maturidade do BAS ou red team automatizado"},
      {layer:4,text:"Automação do monitoramento contínuo de controles"},
      {layer:4,text:"Conformidade com SLA de remediação de vulnerabilidades"},
      {layer:4,text:"Capacidade de purple team e threat hunting"},
      {layer:5,text:"Completude do programa de métricas de segurança"},
      {layer:5,text:"Processo de revisão pós-incidente e lições aprendidas"},
      {layer:5,text:"Roadmap do programa de segurança e acompanhamento de KPIs"},
      {layer:5,text:"Frequência de atualização do modelo de ameaças"},
      {layer:5,text:"Consolidação e otimização de ferramentas de segurança"},
    ],
  }
};

// ─── GEOLOCATION ─────────────────────────────────────────────────────────────
async function detectCountry(langFallback) {
  try {
    const cached = sessionStorage.getItem('s4t_country');
    if (cached && /^[A-Z]{2}$/.test(cached)) return cached;
  } catch {}
  try {
    const res = await fetch('https://ipapi.co/json/');
    if (res.ok) {
      const data = await res.json();
      if (data && data.country_code && /^[A-Z]{2}$/.test(data.country_code)) {
        try { sessionStorage.setItem('s4t_country', data.country_code); } catch {}
        return data.country_code;
      }
    }
  } catch {}
  return langFallback === "pt" ? "BR" : "US";
}

// ─── SCORE ───────────────────────────────────────────────────────────────────
function calcScore(answers, mode, lang) {
  const questions = mode === "exec" ? T[lang].execQuestions : T[lang].techQuestions;
  const ls = [0,0,0,0,0,0], lc = [0,0,0,0,0,0];
  answers.forEach((val, idx) => {
    const layer = questions[idx]?.layer ?? 0;
    const norm = mode === "exec" ? (val === 1 ? 1 : 0) : (val - 1) / 4;
    ls[layer] += norm; lc[layer]++;
  });
  const L = ls.map((s,i) => lc[i] > 0 ? s/lc[i] : 0);
  const C=(L[0]+L[2])/2, O=(L[1]+L[3])/2, Va=(L[4]+L[5])/2;
  const R=Math.max(0.01, 1-(C+O+Va)/3);
  const score=Math.min(1,(Math.pow(C,0.25)*Math.pow(O,0.25)*Math.pow(Va,0.25))/Math.pow(R,0.25));
  return {score,L,C,O,Va};
}

function getMaturity(score, labels) {
  if(score<0.40) return {label:labels[0],color:"#ef4444"};
  if(score<0.65) return {label:labels[1],color:"#f59e0b"};
  if(score<0.80) return {label:labels[2],color:"#10b981"};
  return {label:labels[3],color:"#6366f1"};
}

// ─── SHARE LINK ──────────────────────────────────────────────────────────────
function encodeShareData(score, L) {
  try {
    const d = { s: Math.round(score*100), l: L.map(v => Math.round(v*100)) };
    return btoa(JSON.stringify(d)).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
  } catch { return null; }
}

function decodeShareData(enc) {
  try {
    let s = enc.replace(/-/g,'+').replace(/_/g,'/');
    while(s.length%4) s+='=';
    const d = JSON.parse(atob(s));
    if(typeof d.s!=='number'||!Array.isArray(d.l)||d.l.length!==6) return null;
    return { score: d.s/100, L: d.l.map(v=>v/100) };
  } catch { return null; }
}

async function copyShareLink(score, L) {
  const enc = encodeShareData(score, L);
  if(!enc) return false;
  const url = `${window.location.origin}${window.location.pathname}?r=${enc}`;
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = url;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); document.body.removeChild(ta); return true; }
    catch { document.body.removeChild(ta); return false; }
  }
}

// ─── EMAIL ───────────────────────────────────────────────────────────────────
function openEmail(score, L, recs, t) {
  const isPt = t === T.pt;
  const maturity = getMaturity(score, t.scoreLabel);
  const layerCodes = ["G","RI","CF","OS","VA","L"];

  const layersTxt = t.layers.map((name,i) =>
    `${layerCodes[i]} · ${name}: ${(L[i]*100).toFixed(0)}%`
  ).join("\n");

  const recsTxt = recs.map((rec,i) =>
    `${i+1}. [${rec.priority}] ${rec.title}\n   ${rec.description}`
  ).join("\n\n");

  const body = isPt
    ? `Olá,\n\nAcabei de completar o S4T Framework Security Assessment.\n\n=== MEUS RESULTADOS ===\nSecurity Score: ${(score*100).toFixed(0)}%\nNível de Maturidade: ${maturity.label}\n\n=== DETALHAMENTO POR CAMADA ===\n${layersTxt}\n\n=== PRINCIPAIS RECOMENDAÇÕES ===\n${recsTxt}\n\n--\nGerado pelo S4T Framework Assessment Tool\nhttps://sec4.tech/assessment\nCriado por Diego Neuber`
    : `Hi,\n\nI just completed the S4T Framework Security Assessment.\n\n=== MY RESULTS ===\nSecurity Score: ${(score*100).toFixed(0)}%\nMaturity Level: ${maturity.label}\n\n=== LAYER BREAKDOWN ===\n${layersTxt}\n\n=== TOP RECOMMENDATIONS ===\n${recsTxt}\n\n--\nGenerated by S4T Framework Assessment Tool\nhttps://sec4.tech/assessment\nCreated by Diego Neuber`;

  window.location.href = `mailto:?subject=${encodeURIComponent(t.emailSubject)}&body=${encodeURIComponent(body)}`;
}

// ─── PDF (PRINT-TO-PDF) ───────────────────────────────────────────────────────
function printAsPdf(score, L, recs, t) {
  const isPt = t === T.pt;
  const maturity = getMaturity(score, t.scoreLabel);
  const date = new Date().toLocaleDateString(isPt ? 'pt-BR' : 'en-US');
  const layerCodes = ["G","RI","CF","OS","VA","L"];

  const layersHtml = t.layers.map((name,i) => `
    <tr>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;font-weight:700;color:#6366f1;width:44px">${layerCodes[i]}</td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb">${name}</td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:700">${(L[i]*100).toFixed(0)}%</td>
    </tr>`).join("");

  const recsHtml = recs.map((rec,i) => `
    <div style="margin-bottom:14px;padding:12px 14px;border-left:3px solid #6366f1;background:#f8f8ff;border-radius:0 6px 6px 0">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:5px">
        <strong style="font-size:13px">${i+1}. ${rec.title}</strong>
        <span style="font-size:10px;background:#6366f120;color:#6366f1;padding:2px 8px;border-radius:20px;font-weight:700;white-space:nowrap;margin-left:8px">${rec.priority}</span>
      </div>
      <div style="font-size:12px;color:#555;line-height:1.6">${rec.description}</div>
      <div style="font-size:10px;color:#999;margin-top:5px">📍 ${rec.layer}</div>
    </div>`).join("");

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>S4T Assessment — ${date}</title>
<style>
  @media print{@page{margin:1.5cm}}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;max-width:760px;margin:0 auto;padding:24px;color:#111;line-height:1.55;font-size:14px}
  h1{color:#6366f1;font-size:22px;margin:0 0 4px}
  .meta{color:#888;font-size:12px;margin-bottom:28px}
  .score-box{text-align:center;padding:24px;border:2px solid ${maturity.color};border-radius:10px;margin-bottom:28px;background:${maturity.color}10}
  .score-num{font-size:52px;font-weight:900;color:${maturity.color};line-height:1}
  .score-lbl{font-size:13px;color:${maturity.color};text-transform:uppercase;letter-spacing:2px;margin-top:8px;font-weight:700}
  h2{font-size:13px;color:#555;margin:24px 0 10px;text-transform:uppercase;letter-spacing:1px;border-bottom:1px solid #eee;padding-bottom:6px}
  table{width:100%;border-collapse:collapse;margin-bottom:20px}
  .footer{margin-top:40px;padding-top:16px;border-top:1px solid #ddd;font-size:11px;color:#aaa;text-align:center}
  .footer a{color:#6366f1;text-decoration:none}
</style></head><body>
  <h1>S4T Framework · Security Assessment</h1>
  <div class="meta">${isPt?"Gerado em":"Generated"}: ${date}</div>
  <div class="score-box">
    <div style="font-size:10px;text-transform:uppercase;letter-spacing:2px;color:#888;margin-bottom:8px">${t.yourScore}</div>
    <div class="score-num">${(score*100).toFixed(0)}%</div>
    <div class="score-lbl">${maturity.label}</div>
  </div>
  <h2>${isPt?"Pontuação por Camada":"Layer Breakdown"}</h2>
  <table>${layersHtml}</table>
  <h2>${t.recommendations}</h2>
  ${recsHtml}
  <div class="footer">
    S4T Framework v3.0 · Diego Neuber<br>
    <a href="https://sec4.tech/assessment">sec4.tech/assessment</a> &nbsp;·&nbsp;
    <a href="https://github.com/diegoneuber/S4T-Framework">github.com/diegoneuber/S4T-Framework</a>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},400);};</script>
</body></html>`;

  const w = window.open('','_blank');
  if(w){ w.document.write(html); w.document.close(); }
}

// ─── RADAR ───────────────────────────────────────────────────────────────────
function RadarChart({scores,labels,color}) {
  const cx=120,cy=115,r=78,n=scores.length;
  const pt=(s,i)=>{const a=(i*2*Math.PI/n)-Math.PI/2;return{x:cx+r*s*Math.cos(a),y:cy+r*s*Math.sin(a)};};
  const grid=(f)=>Array.from({length:n},(_,i)=>{const a=(i*2*Math.PI/n)-Math.PI/2;return`${cx+r*f*Math.cos(a)},${cy+r*f*Math.sin(a)}`;}).join(" ");
  const pts=scores.map((s,i)=>pt(s,i));
  return(
    <svg viewBox="0 0 240 230" style={{width:"100%",maxWidth:260}}>
      {[0.25,0.5,0.75,1].map(f=><polygon key={f} points={grid(f)} fill="none" stroke="rgba(10,26,47,0.12)" strokeWidth="1"/>)}
      {Array.from({length:n},(_,i)=>{const a=(i*2*Math.PI/n)-Math.PI/2;return<line key={i} x1={cx} y1={cy} x2={cx+r*Math.cos(a)} y2={cy+r*Math.sin(a)} stroke="rgba(10,26,47,0.12)" strokeWidth="1"/>;} )}
      <polygon points={pts.map(p=>`${p.x},${p.y}`).join(" ")} fill={`${color}22`} stroke={color} strokeWidth="2"/>
      {pts.map((p,i)=><circle key={i} cx={p.x} cy={p.y} r="3.5" fill={color}/>)}
      {Array.from({length:n},(_,i)=>{
        const a=(i*2*Math.PI/n)-Math.PI/2;
        const lx=cx+(r+20)*Math.cos(a),ly=cy+(r+20)*Math.sin(a);
        const words=labels[i].split(" ");
        return(<text key={i} textAnchor="middle" fill="rgba(10,26,47,0.6)" fontSize="7.5">
          {words.map((w,wi)=><tspan key={wi} x={lx} y={ly+(wi-(words.length-1)/2)*10}>{w}</tspan>)}
        </text>);
      })}
    </svg>
  );
}

// ─── GAUGE ───────────────────────────────────────────────────────────────────
function Gauge({score,color,label}) {
  const pct=Math.min(100,Math.max(0,score*100));
  const toXY=(deg,r)=>({x:110+r*Math.cos(deg*Math.PI/180),y:110+r*Math.sin(deg*Math.PI/180)});
  const startA=-220,endA=40;
  const curA=startA+(pct/100)*(endA-startA);
  const arcPath=(s,e,r)=>{const sp=toXY(s,r),ep=toXY(e,r);const diff=((e-s)+360)%360;const lg=diff>180?1:0;return`M ${sp.x} ${sp.y} A ${r} ${r} 0 ${lg} 1 ${ep.x} ${ep.y}`;};
  const needle=toXY(curA,60);
  return(
    <svg viewBox="0 0 220 160" style={{width:"100%",maxWidth:240}}>
      <path d={arcPath(startA,endA,75)} fill="none" stroke="rgba(10,26,47,0.1)" strokeWidth="14" strokeLinecap="round"/>
      {pct>0&&<path d={arcPath(startA,curA,75)} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round"/>}
      <line x1={110} y1={110} x2={needle.x} y2={needle.y} stroke="#0a1a2f" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx={110} cy={110} r="5" fill="#0a1a2f"/>
      <text x={110} y={138} textAnchor="middle" fill="#0a1a2f" fontSize="24" fontWeight="800">{pct.toFixed(0)}%</text>
      <text x={110} y={152} textAnchor="middle" fill={color} fontSize="9" fontWeight="700" letterSpacing="1">{label.toUpperCase()}</text>
    </svg>
  );
}

// ─── METRICS PANEL ───────────────────────────────────────────────────────────
function MetricsPanelWithHeader({t}) {
  const [m,setM]=useState({total:0,avg:0,countries:0,codes:[],loaded:false});

  useEffect(()=>{
    fetch(METRICS_URL)
      .then(r=>r.json())
      .then(data=>setM({
        total:data.total||0,
        avg:data.avg||0,
        countries:data.countries||0,
        codes:data.countryCodes||[],
        loaded:true
      }))
      .catch(()=>setM(p=>({...p,loaded:true})));
  },[]);

  if(!m.loaded||m.total<METRICS_THRESHOLD) return null;

  const proofText = t.socialProof.replace("{N}",m.total).replace("{C}",m.countries);

  const flagEmoji = (code) => {
    if(!code||code.length!==2) return null;
    const c = code.toUpperCase();
    return String.fromCodePoint(
      c.charCodeAt(0)-65+127462,
      c.charCodeAt(1)-65+127462
    );
  };

  return(
    <div style={{textAlign:"center",marginBottom:8}}>
      <div style={{fontSize:fs(11),textTransform:"uppercase",letterSpacing:2,color:"#5a6b7d",marginBottom:10,fontFamily:"'IBM Plex Mono',monospace"}}>{t.metrics}</div>
      <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap",marginBottom:10}}>
        {[
          {l:t.totalAssessments,v:m.total},
          {l:t.countries,v:m.countries},
          {l:t.avgScore,v:`${(m.avg*100).toFixed(0)}%`}
        ].map(x=>(
          <div key={x.l} style={{background:"#ffffff",border:"1px solid rgba(10,26,47,0.12)",borderRadius:6,padding:"10px 20px",textAlign:"center"}}>
            <div style={{fontSize:26,fontWeight:900,color:"#6366f1",fontFamily:"monospace"}}>{x.v}</div>
            <div style={{fontSize:fs(11),color:"#5a6b7d",textTransform:"uppercase",letterSpacing:1,marginTop:2}}>{x.l}</div>
          </div>
        ))}
      </div>

    {m.codes.length>0&&(
  <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap",marginBottom:10}}>
    {m.codes.map(code=>(
      <img
        key={code}
        src={`https://flagcdn.com/24x18/${code.toLowerCase()}.png`}
        alt={code}
        title={code}
        style={{
          width:24,
          height:18,
          borderRadius:2,
          objectFit:"cover",
          boxShadow:"0 1px 3px rgba(0,0,0,0.4)"
        }}
      />
    ))}
  </div>
)}

      <div style={{fontSize:fs(12),color:"#5a6b7d",fontStyle:"italic",maxWidth:460,margin:"0 auto",lineHeight:1.5}}>{proofText}</div>
    </div>
  );
}

// ─── ICON COMPONENTS ─────────────────────────────────────────────────────────
const IconEmail = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const IconDownload = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const IconLink = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>;
const IconGitHub = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>;
const IconMsg = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function S4TAssessment() {
  const [lang,setLang]=useState("en");
  const [screen,setScreen]=useState("home");
  const [mode,setMode]=useState(null);
  const [current,setCurrent]=useState(0);
  const [answers,setAnswers]=useState([]);
  const [result,setResult]=useState(null);
  const [isShared,setIsShared]=useState(false);
  const [toast,setToast]=useState(null);
  const [lead,setLead]=useState({name:"",email:"",company:"",phone:"",consent:false});
  const [leadState,setLeadState]=useState("idle"); // idle | sending | ok | error

  const t=T[lang];
  const questions=mode?(mode==="exec"?t.execQuestions:t.techQuestions):[];
  const totalQ=questions.length;
  const lc=["#12385f","#1c5f9e","#2f7bc4","#4a9fe0","#b08a3e","#5a6b7d"];
  const pc={"High":"#b0483e","Alta":"#b0483e","Medium":"#b08a3e","Média":"#b08a3e","Low":"#1f7a54","Baixa":"#1f7a54"};

  const app = {
  minHeight:"100vh",
  background:"#f5f3ee",
  color:"#0a1a2f",
  fontFamily:"'Inter','Segoe UI',sans-serif",
  fontSize: "clamp(15px, 1.05vw, 18px)",
};
  const glass={background:"#ffffff",border:"1px solid rgba(10,26,47,0.12)",borderRadius:6,padding:"24px"};
  const btn=(c="#12385f",o=false)=>({background:o?"transparent":c,border:o?`1.5px solid ${c}`:"none",color:o?c:"#f5f3ee",padding:"12px 26px",borderRadius:3,fontSize: fs(14),fontWeight:600,fontFamily:"'Inter',sans-serif",cursor:"pointer",transition:"all 0.15s"});
  const actionBtn=(c,bc)=>({padding:"11px 10px",borderRadius:3,border:`1px solid ${bc}`,background:"#ffffff",color:c,fontWeight:600,fontSize:fs(13),cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:7,transition:"all 0.15s",flex:1});
  const leadInput={width:"100%",fontFamily:"'Inter',sans-serif",fontSize:fs(14),color:"#0a1a2f",background:"#ffffff",border:"1px solid rgba(10,26,47,0.18)",borderRadius:3,padding:"10px 12px",boxSizing:"border-box"};

  // Detect shared link on mount
  useEffect(()=>{
    try{
      const params=new URLSearchParams(window.location.search);
      const r=params.get('r');
      if(r){
        const d=decodeShareData(r);
        if(d){setResult(d);setIsShared(true);setScreen("results");}
      }
    }catch{}
  },[]);

  // Auto-dismiss toast
  useEffect(()=>{
    if(toast){const tm=setTimeout(()=>setToast(null),2500);return()=>clearTimeout(tm);}
  },[toast]);

  function showToast(msg){setToast(msg);}
  function start(m){setMode(m);setCurrent(0);setAnswers([]);setResult(null);setIsShared(false);setScreen("questions");}
  function goHome(){setScreen("home");setResult(null);setIsShared(false);try{window.history.replaceState({},"",window.location.pathname);}catch{}}

  function answer(val){
    const a=[...answers];a[current]=val;setAnswers(a);
    if(current<totalQ-1)setCurrent(current+1);
  }

  async function finish(){
    const r=calcScore(answers,mode,lang);
    setResult(r);
    setScreen("results");
    const country=await detectCountry(lang);
    try{
      await fetch(METRICS_URL,{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({score:r.score,country,mode,lang})
      });
    }catch{}
  }

  const maturity=result?getMaturity(result.score,t.scoreLabel):null;
  const canFinish=current===totalQ-1&&answers[current]!==undefined;
  const canNext=answers[current]!==undefined&&current<totalQ-1;

  async function submitLead(){
    if(!lead.consent){ showToast(t.leadConsentReq); return; }
    if(!lead.name.trim()||!lead.email.trim()){ return; }
    setLeadState("sending");
    let country="";
    try{ country=await detectCountry(lang); }catch{}
    try{
      const res=await fetch(LEADS_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          name:lead.name.trim(),
          email:lead.email.trim(),
          company:lead.company.trim(),
          phone:lead.phone.trim(),
          consent:true,
          score:result?result.score:null,
          mode,
          lang,
          country,
          ts:new Date().toISOString()
        })
      });
      const j=await res.json().catch(()=>({}));
      if(res.ok&&j&&j.success){
        setLeadState("ok");
        // também gera o PDF para a pessoa na hora
        try{ printAsPdf(result.score,result.L,getStaticRecommendations(result.L,lang),t); }catch{}
      } else {
        setLeadState("error");
      }
    }catch{
      setLeadState("error");
    }
  }

  // ── HOME ──────────────────────────────────────────────────────────────────
  if(screen==="home") return(
    <div style={app}>
      <div style={{maxWidth:640,margin:"0 auto",padding:"40px 20px"}}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:36}}>
          <a href="https://sec4.tech" style={{display:"flex",alignItems:"center",gap:12,textDecoration:"none"}}>
            <img src="shield.png" alt="Sec4Tech" style={{width:42,height:42,display:"block"}}/>
            <div>
              <div style={{fontSize:20,fontWeight:600,letterSpacing:-0.3,fontFamily:"'Fraunces',serif",color:"#0a1a2f"}}>{t.title}</div>
              <div style={{fontSize:fs(11),color:"#5a6b7d",letterSpacing:2,textTransform:"uppercase",fontFamily:"'IBM Plex Mono',monospace"}}>{t.subtitle}</div>
            </div>
          </a>
          <button onClick={()=>setLang(lang==="en"?"pt":"en")} style={{...btn("#12385f",true),padding:"7px 15px",fontSize:fs(13)}}>{t.switchLang}</button>
        </div>

        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:30,fontWeight:500,letterSpacing:-0.5,marginBottom:6,fontFamily:"'Fraunces',serif",color:"#0a1a2f"}}>{t.tagline}</div>
          <div style={{fontSize:fs(13),color:"#5a6b7d",fontFamily:"'IBM Plex Mono',monospace"}}>S4T = (G, RI, CF, OS, VA, L) · v3.0 · Diego Neuber</div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:24}}>
          {[
            {m:"exec",icon:"📊",title:t.startExecutive,desc:t.execDesc,c:"#12385f"},
            {m:"tech",icon:"⚙️",title:t.startTechnical,desc:t.techDesc,c:"#1c5f9e"}
          ].map(o=>(
            <button key={o.m} onClick={()=>start(o.m)} style={{...glass,borderTop:`3px solid ${o.c}`,cursor:"pointer",textAlign:"left",background:"#ffffff",padding:"22px"}}>
              <div style={{fontSize:26,marginBottom:9}}>{o.icon}</div>
              <div style={{fontSize:fs(15),fontWeight:700,marginBottom:4,color:o.c,fontFamily:"'Fraunces',serif"}}>{o.title}</div>
              <div style={{fontSize:fs(12),color:"#5a6b7d",lineHeight:1.6}}>{o.desc}</div>
            </button>
          ))}
        </div>

        <div style={{...glass,marginBottom:20}}>
          <div style={{fontSize:fs(11),textTransform:"uppercase",letterSpacing:2,color:"#1c5f9e",marginBottom:12,fontFamily:"'IBM Plex Mono',monospace"}}>{t.frameworkArch}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:7}}>
            {t.layers.map((l,i)=>(
              <div key={i} style={{background:"#f5f3ee",borderRadius:4,padding:"8px 10px",borderLeft:`3px solid ${lc[i]}`}}>
                <div style={{fontSize:9,fontWeight:700,marginBottom:2,color:lc[i]}}>{["G","RI","CF","OS","VA","L"][i]} · {l}</div>
                <div style={{fontSize:fs(11),color:"#5a6b7d",lineHeight:1.4}}>{t.layerDesc[i]}</div>
              </div>
            ))}
          </div>
        </div>

        <MetricsPanelWithHeader t={t}/>

        <div style={{display:"flex",gap:16,justifyContent:"center",marginTop:24,flexWrap:"wrap"}}>
          <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer"
            style={{fontSize:fs(12),color:"#5a6b7d",textDecoration:"none",display:"inline-flex",alignItems:"center",gap:6}}>
            <IconGitHub/>{t.viewGithub}
          </a>
          <a href={`mailto:${FEEDBACK_EMAIL}`}
            style={{fontSize:fs(12),color:"#5a6b7d",textDecoration:"none",display:"inline-flex",alignItems:"center",gap:6}}>
            <IconMsg/>{t.feedback}
          </a>
        </div>

        <div style={{textAlign:"center",marginTop:20,fontSize:fs(11),color:"#9aa5b1"}}>S4T Framework v3.0 · Diego Neuber · sec4.tech</div>
      </div>
    </div>
  );

  // ── QUESTIONS ─────────────────────────────────────────────────────────────
  if(screen==="questions"){
    const q=questions[current];
    const isExec=mode==="exec";
    const cur=answers[current];
    const prog=(current/totalQ)*100;
    return(
      <div style={app}>
        <div style={{maxWidth:540,margin:"0 auto",padding:"34px 20px"}}>
          <div style={{marginBottom:24}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:fs(12),color:"#5a6b7d",marginBottom:6}}>
              <span style={{color:lc[q.layer],fontWeight:600}}>{["G","RI","CF","OS","VA","L"][q.layer]} · {t.layers[q.layer]}</span>
              <span>{current+1} / {totalQ}</span>
            </div>
            <div style={{height:4,background:"rgba(10,26,47,0.1)",borderRadius:99}}>
              <div style={{height:"100%",width:`${prog}%`,background:"linear-gradient(90deg,#12385f,#2f7bc4)",borderRadius:99,transition:"width 0.3s"}}/>
            </div>
          </div>

          <div style={{...glass,marginBottom:18}}>
            <div style={{fontSize:fs(18),fontWeight:600,lineHeight:1.55,marginBottom:24,fontFamily:"'Fraunces',serif",color:"#0a1a2f"}}>{q.text}</div>
            {isExec?(
              <div style={{display:"flex",gap:10}}>
                {[1,0].map(val=>(
                  <button key={val} onClick={()=>answer(val)} style={{flex:1,padding:"14px",borderRadius:3,border:`1.5px solid ${cur===val?(val?"#1f7a54":"#b0483e"):"rgba(10,26,47,0.15)"}`,background:cur===val?(val?"#1f7a5412":"#b0483e12"):"#ffffff",color:cur===val?(val?"#1f7a54":"#b0483e"):"#3d4f61",fontWeight:600,fontSize:15,fontFamily:"'Inter',sans-serif",cursor:"pointer",transition:"all 0.15s"}}>
                    {val?t.yes:t.no}
                  </button>
                ))}
              </div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {[1,2,3,4,5].map(val=>(
                  <button key={val} onClick={()=>answer(val)} style={{padding:"11px 13px",borderRadius:3,border:`1.5px solid ${cur===val?"#1c5f9e":"rgba(10,26,47,0.12)"}`,background:cur===val?"#1c5f9e10":"#ffffff",color:cur===val?"#12385f":"#3d4f61",fontWeight:cur===val?600:400,fontSize:fs(14),cursor:"pointer",transition:"all 0.15s",textAlign:"left",display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:fs(11),color:cur===val?"#1c5f9e":"#9aa5b1",minWidth:12,fontFamily:"'IBM Plex Mono',monospace"}}>{val}</span>
                    {t.scaleLabels[val-1]}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>current>0?setCurrent(current-1):setScreen("home")} style={btn("#12385f",true)}>{t.back}</button>
            {canNext&&<button onClick={()=>setCurrent(current+1)} style={{...btn("#12385f"),flex:1}}>{t.next}</button>}
            {canFinish&&<button onClick={finish} style={{...btn("#1c5f9e"),flex:1}}>{t.finish}</button>}
          </div>
        </div>
      </div>
    );
  }

  // ── RESULTS ───────────────────────────────────────────────────────────────
  if(screen==="results"&&result){
    const {score,L}=result;
    const recs=getStaticRecommendations(L,lang);
    const above65=score>=0.65;

    return(
      <div style={app}>
        <div style={{maxWidth:680,margin:"0 auto",padding:"34px 20px"}}>

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
            <div style={{fontSize:fs(12),textTransform:"uppercase",letterSpacing:2,color:"#5a6b7d",fontFamily:"'IBM Plex Mono',monospace"}}>{t.resultsLabel}</div>
            <button onClick={()=>setLang(lang==="en"?"pt":"en")} style={{...btn("#12385f",true),padding:"6px 13px",fontSize:fs(12)}}>{t.switchLang}</button>
          </div>

          {/* Shared banner */}
          {isShared&&(
            <div style={{...glass,marginBottom:16,background:"#eaf1f8",border:"1px solid rgba(28,95,158,0.25)",padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
              <div style={{fontSize:fs(13),color:"#3d4f61"}}>🔗 {t.sharedNotice}</div>
              <button onClick={goHome} style={{...btn("#12385f"),padding:"8px 14px",fontSize:fs(13)}}>{t.takeYourOwn}</button>
            </div>
          )}

          {/* Score card */}
          <div style={{...glass,textAlign:"center",marginBottom:16,borderTop:`3px solid ${maturity.color}`}}>
            <div style={{fontSize:fs(12),textTransform:"uppercase",letterSpacing:2,color:"#5a6b7d",marginBottom:12,fontFamily:"'IBM Plex Mono',monospace"}}>{t.yourScore}</div>
            <div style={{maxWidth:210,margin:"0 auto"}}><Gauge score={score} color={maturity.color} label={maturity.label}/></div>
            <div style={{marginTop:8,fontSize:fs(12),fontWeight:600,color:above65?"#1f7a54":"#b08a3e"}}>
              {above65?t.benchmarkAbove:t.benchmarkBelow}
            </div>
          </div>

          {/* Radar + bars */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <div style={{...glass,display:"flex",alignItems:"center",justifyContent:"center",padding:14}}>
              <RadarChart scores={L} labels={t.layers} color="#1c5f9e"/>
            </div>
            <div style={glass}>
              {t.layers.map((name,i)=>(
                <div key={i} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:fs(11),marginBottom:3}}>
                    <span style={{color:lc[i],fontWeight:700}}>{["G","RI","CF","OS","VA","L"][i]}</span>
                    <span style={{color:"#5a6b7d"}}>{(L[i]*100).toFixed(0)}%</span>
                  </div>
                  <div style={{height:5,background:"rgba(10,26,47,0.08)",borderRadius:99}}>
                    <div style={{height:"100%",width:`${L[i]*100}%`,background:lc[i],borderRadius:99,transition:"width 1s ease"}}/>
                  </div>
                  <div style={{fontSize:9,color:"#5a6b7d",marginTop:2}}>{name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div style={{...glass,marginBottom:16}}>
            <div style={{fontSize:fs(12),textTransform:"uppercase",letterSpacing:2,color:"#1c5f9e",marginBottom:16,fontFamily:"'IBM Plex Mono',monospace"}}>📋 {t.recommendations}</div>
            {recs.map((rec,i)=>(
              <div key={i} style={{background:"#f5f3ee",borderRadius:4,padding:"13px",marginBottom:9,borderLeft:`3px solid ${pc[rec.priority]||"#1c5f9e"}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div style={{fontSize:fs(14),fontWeight:600,paddingRight:10,fontFamily:"'Fraunces',serif",color:"#0a1a2f"}}>{rec.title}</div>
                  <span style={{fontSize:9,background:`${pc[rec.priority]}1a`,color:pc[rec.priority],padding:"3px 8px",borderRadius:99,fontWeight:700,letterSpacing:1,whiteSpace:"nowrap"}}>{rec.priority}</span>
                </div>
                <div style={{fontSize:fs(13),color:"#3d4f61",lineHeight:1.65}}>{rec.description}</div>
                <div style={{fontSize:fs(11),color:"#5a6b7d",marginTop:6}}>📍 {rec.layer}</div>
              </div>
            ))}
          </div>

          {/* Maturity benchmarks */}
          <div style={{...glass,marginBottom:16}}>
            <div style={{fontSize:fs(11),textTransform:"uppercase",letterSpacing:2,color:"#5a6b7d",marginBottom:10,fontFamily:"'IBM Plex Mono',monospace"}}>{t.maturityBench}</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
              {[
                {r:"< 40%",l:t.scoreLabel[0],c:"#b0483e",min:0,max:0.4},
                {r:"40–65%",l:t.scoreLabel[1],c:"#b08a3e",min:0.4,max:0.65},
                {r:"65–80%",l:t.scoreLabel[2],c:"#1f7a54",min:0.65,max:0.8},
                {r:"80%+",l:t.scoreLabel[3],c:"#1c5f9e",min:0.8,max:1.01}
              ].map((m,i)=>{
                const active=score>=m.min&&score<m.max;
                return(<div key={i} style={{textAlign:"center",padding:"9px 5px",borderRadius:4,background:active?`${m.c}15`:"#f5f3ee",border:`1px solid ${active?m.c:"rgba(10,26,47,0.06)"}`}}>
                  <div style={{fontSize:fs(11),fontWeight:800,color:m.c}}>{m.r}</div>
                  <div style={{fontSize:fs(11),color:"#5a6b7d",marginTop:2}}>{m.l}</div>
                </div>);
              })}
            </div>
          </div>

          {/* Lead capture — full report */}
          {!isShared&&(
          <div style={{...glass,marginBottom:16,borderTop:"3px solid #1c5f9e"}}>
            {leadState==="ok"?(
              <div style={{textAlign:"center",padding:"12px 6px"}}>
                <div style={{fontSize:34,marginBottom:8}}>✓</div>
                <div style={{fontSize:fs(15),fontWeight:600,color:"#1f7a54",fontFamily:"'Fraunces',serif"}}>{t.leadOk}</div>
              </div>
            ):(
              <>
                <div style={{fontSize:fs(17),fontWeight:600,color:"#0a1a2f",fontFamily:"'Fraunces',serif",marginBottom:6}}>{t.leadTitle}</div>
                <div style={{fontSize:fs(13),color:"#3d4f61",lineHeight:1.6,marginBottom:16}}>{t.leadDesc}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <input value={lead.name} onChange={e=>setLead({...lead,name:e.target.value})} placeholder={t.leadName}
                    style={leadInput}/>
                  <input value={lead.company} onChange={e=>setLead({...lead,company:e.target.value})} placeholder={t.leadCompany}
                    style={leadInput}/>
                  <input value={lead.email} onChange={e=>setLead({...lead,email:e.target.value})} placeholder={t.leadEmail} type="email"
                    style={leadInput}/>
                  <input value={lead.phone} onChange={e=>setLead({...lead,phone:e.target.value})} placeholder={t.leadPhone} type="tel"
                    style={leadInput}/>
                </div>
                <label style={{display:"flex",alignItems:"flex-start",gap:8,fontSize:fs(12),color:"#3d4f61",lineHeight:1.5,marginBottom:14,cursor:"pointer"}}>
                  <input type="checkbox" checked={lead.consent} onChange={e=>setLead({...lead,consent:e.target.checked})}
                    style={{marginTop:3,width:16,height:16,accentColor:"#1c5f9e",flexShrink:0}}/>
                  <span>{t.leadConsent} <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{color:"#1c5f9e",textDecoration:"underline"}}>{t.leadPrivacy}</a>.</span>
                </label>
                <button onClick={submitLead} disabled={leadState==="sending"}
                  style={{...btn("#1c5f9e"),width:"100%",opacity:leadState==="sending"?0.6:1,cursor:leadState==="sending"?"not-allowed":"pointer"}}>
                  {leadState==="sending"?t.leadSending:t.leadSubmit}
                </button>
                {leadState==="error"&&(
                  <div style={{fontSize:fs(12),color:"#b0483e",marginTop:10,textAlign:"center"}}>{t.leadErr}</div>
                )}
              </>
            )}
          </div>
          )}

          {/* Actions: Email / Share */}
          <div style={{...glass,marginBottom:16}}>
            <div style={{fontSize:fs(11),textTransform:"uppercase",letterSpacing:2,color:"#5a6b7d",marginBottom:12,fontFamily:"'IBM Plex Mono',monospace"}}>{t.actions}</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button onClick={()=>openEmail(score,L,recs,t)} style={actionBtn("#12385f","rgba(18,56,95,0.3)")}>
                <IconEmail/>{t.emailResults}
              </button>
              <button onClick={async()=>{const ok=await copyShareLink(score,L);if(ok)showToast(t.linkCopied);}} style={actionBtn("#b08a3e","rgba(176,138,62,0.3)")}>
                <IconLink/>{t.shareLink}
              </button>
            </div>
          </div>

          {/* Nav buttons */}
          <div style={{display:"flex",gap:10,marginBottom:20}}>
            <button onClick={goHome} style={{...btn("#12385f"),flex:1}}>{t.restart}</button>
            <button onClick={()=>setLang(lang==="en"?"pt":"en")} style={btn("#12385f",true)}>{t.switchLang}</button>
          </div>

          {/* Open source footer */}
          <div style={{textAlign:"center",padding:"16px 0",borderTop:"1px solid rgba(10,26,47,0.08)"}}>
            <div style={{fontSize:fs(12),color:"#5a6b7d",marginBottom:10}}>{t.openSource}</div>
            <div style={{display:"flex",gap:18,justifyContent:"center",flexWrap:"wrap"}}>
              <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer"
                style={{fontSize:fs(12),color:"#1c5f9e",textDecoration:"none",display:"inline-flex",alignItems:"center",gap:6}}>
                <IconGitHub/>{t.contributeGithub}
              </a>
              <a href={`mailto:${FEEDBACK_EMAIL}`}
                style={{fontSize:fs(12),color:"#1c5f9e",textDecoration:"none",display:"inline-flex",alignItems:"center",gap:6}}>
                <IconMsg/>{t.feedback}
              </a>
            </div>
          </div>

          <div style={{textAlign:"center",marginTop:16,fontSize:fs(11),color:"#9aa5b1"}}>S4T Framework v3.0 · Diego Neuber · sec4.tech</div>
        </div>

        {/* Toast */}
        {toast&&(
          <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",background:"#12385f",color:"#fff",padding:"12px 22px",borderRadius:4,fontSize:fs(14),fontWeight:600,boxShadow:"0 4px 20px rgba(10,26,47,0.25)",zIndex:1000,whiteSpace:"nowrap"}}>
            ✓ {toast}
          </div>
        )}
      </div>
    );
  }

  return null;
}
